import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, fmtNum } from "../components/Shared";
import {
  Pill, Warehouse, ClipboardCheck, TrendingDown, TrendingUp, CheckCircle2,
  ArrowRight, CircleDashed, Lock, Building2, AlertTriangle, CalendarClock, PackageX,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, tint, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 transition-colors" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <div className="mt-4 font-head text-3xl font-extrabold text-slate-900 tabular">{value}</div>
    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">{label}</div>
  </div>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [gudang, setGudang] = useState([]);
  const [lock, setLock] = useState(null);
  const [temuan, setTemuan] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/stats").then((r) => setS(r.data)).catch(() => {});
    api.get("/gudang-progress").then((r) => setGudang(r.data)).catch(() => {});
    api.get("/so-lock").then((r) => setLock(r.data)).catch(() => {});
    api.get("/temuan").then((r) => setTemuan(r.data)).catch(() => {});
  }, []);

  const progress = s && s.total_obat ? Math.round((s.total_counted / s.total_obat) * 100) : 0;

  return (
    <div className="pb-12">
      <PageHeader title="Dashboard" subtitle="Ringkasan kondisi stock opname farmasi" />
      <div className="px-6 lg:px-8 pt-6 space-y-6">
        {lock?.locked && (
          <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="dashboard-lock-banner">
            <Lock className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-bold">Periode SO Terkunci</span>
              {lock.period ? ` · ${lock.period}` : ""} — perhitungan tidak dapat diubah.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Pill} label="Total Master Obat" value={fmtNum(s?.total_obat ?? 0)} tint="bg-teal-50 text-teal-700" testid="stat-total-obat" />
          <Stat icon={Warehouse} label="Tempat Penyimpanan" value={fmtNum(s?.total_lokasi ?? 0)} tint="bg-sky-50 text-sky-700" testid="stat-total-lokasi" />
          <Stat icon={ClipboardCheck} label="Sudah Dihitung" value={fmtNum(s?.total_counted ?? 0)} tint="bg-indigo-50 text-indigo-700" testid="stat-counted" />
          <Stat icon={CircleDashed} label="Belum Dihitung" value={fmtNum(s?.total_uncounted ?? 0)} tint="bg-slate-100 text-slate-600" testid="stat-uncounted" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-bold text-slate-800">Progress Perhitungan</h3>
              <span className="text-sm font-bold text-teal-700 tabular">{progress}%</span>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-teal-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {fmtNum(s?.total_counted ?? 0)} dari {fmtNum(s?.total_obat ?? 0)} item obat telah dihitung fisik.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-6">
              <div className="rounded-lg bg-red-50 border border-red-100 p-4">
                <div className="flex items-center gap-2 text-red-600"><TrendingDown className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Selisih Minus</span></div>
                <div className="mt-2 font-head text-2xl font-extrabold text-red-600 tabular">{fmtNum(s?.minus ?? 0)}</div>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-100 p-4">
                <div className="flex items-center gap-2 text-amber-600"><TrendingUp className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Selisih Plus</span></div>
                <div className="mt-2 font-head text-2xl font-extrabold text-amber-600 tabular">{fmtNum(s?.plus ?? 0)}</div>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-4">
                <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Sesuai</span></div>
                <div className="mt-2 font-head text-2xl font-extrabold text-emerald-600 tabular">{fmtNum(s?.matched ?? 0)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col">
            <h3 className="font-head font-bold text-slate-800 mb-4">Aksi Cepat</h3>
            <div className="space-y-2 flex-1">
              {isAdmin && (
                <Link to="/master-obat" className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 hover:bg-teal-50 text-slate-700 hover:text-teal-800 transition-colors group" data-testid="quick-master">
                  <span className="text-sm font-medium flex items-center gap-2"><Pill className="h-4 w-4" /> Impor Master Obat</span>
                  <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              )}
              {isAdmin && (
                <Link to="/tempat-penyimpanan" className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 hover:bg-teal-50 text-slate-700 hover:text-teal-800 transition-colors group" data-testid="quick-lokasi">
                  <span className="text-sm font-medium flex items-center gap-2"><Warehouse className="h-4 w-4" /> Kelola Tempat</span>
                  <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              )}
              <Link to="/stock-opname" className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 hover:bg-teal-50 text-slate-700 hover:text-teal-800 transition-colors group" data-testid="quick-opname">
                <span className="text-sm font-medium flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Mulai Stock Opname</span>
                <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>
        </div>

        {temuan && (temuan.expiring_count > 0 || temuan.missing_count > 0) && (
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="dashboard-temuan-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h3 className="font-head font-bold text-slate-800">Hasil Temuan Stock Opname</h3>
              </div>
              <Link to="/temuan" className="text-xs font-semibold text-teal-700 hover:text-teal-800 flex items-center gap-1" data-testid="dashboard-temuan-link">Lihat semua <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/temuan" className="rounded-lg border border-amber-100 bg-amber-50 p-4 hover:border-amber-300 transition-colors">
                <div className="flex items-center gap-2 text-amber-700"><CalendarClock className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Akan Kadaluarsa ≤ 3 Bulan</span></div>
                <div className="mt-2 font-head text-2xl font-extrabold text-amber-700 tabular">{fmtNum(temuan.expiring_count)}</div>
              </Link>
              <Link to="/temuan" className="rounded-lg border border-red-100 bg-red-50 p-4 hover:border-red-300 transition-colors">
                <div className="flex items-center gap-2 text-red-600"><PackageX className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Stok Awal Ada, Fisik Tidak Ditemukan</span></div>
                <div className="mt-2 font-head text-2xl font-extrabold text-red-600 tabular">{fmtNum(temuan.missing_count)}</div>
              </Link>
            </div>
            {temuan.missing.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Obat Tidak Ditemukan (stok awal ada, fisik 0)</p>
                <div className="space-y-1.5">
                  {temuan.missing.slice(0, 5).map((m) => (
                    <div key={m.id_barang} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg border border-slate-100 px-3 py-2">
                      <span className="text-slate-700 font-medium truncate mr-2">{m.nama}</span>
                      <span className="text-slate-500 shrink-0">{m.gudang} · Awal {fmtNum(m.total_awal)} → Fisik <b className="text-red-600">0</b></span>
                    </div>
                  ))}
                  {temuan.missing.length > 5 && (
                    <p className="text-[11px] text-slate-400 pt-1">+{temuan.missing.length - 5} obat lainnya…</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="gudang-progress-section">          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-teal-700" />
            <h3 className="font-head font-bold text-slate-800">Progress per Gudang</h3>
          </div>
          {gudang.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Belum ada data gudang / tempat penyimpanan.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {gudang.map((g) => {
                const pct = g.total_tempat ? Math.round((g.tempat_selesai / g.total_tempat) * 100) : 0;
                return (
                  <div key={g.gudang} className="rounded-lg border border-slate-200 p-4" data-testid={`gudang-progress-${g.gudang}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-800 text-sm">{g.gudang || "(Tanpa Gudang)"}</span>
                      <span className="text-xs font-bold text-teal-700 tabular">{g.tempat_selesai}/{g.total_tempat} tempat</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-teal-600 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">{fmtNum(g.total_obat)} item obat · {pct}% tempat selesai</p>
                    <div className="mt-3 space-y-1.5">
                      {g.locations.map((l) => (
                        <div key={l.nama} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-slate-600">
                            {l.selesai ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <CircleDashed className="h-3.5 w-3.5 text-slate-300" />}
                            {l.nama}
                          </span>
                          <span className="tabular text-slate-500">{fmtNum(l.counted)}/{fmtNum(l.total_obat)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
